import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import i18next from 'i18next';
import {showNotification} from 'platform/components';

import {not} from 'ramda';
import {isPlainObj} from 'ramda-adjunct';

import {browserStorageKey} from '@dms/config';

import {BaseQueryExtraOptions, parseZodSchema, ParseZodSchemaQueryInfo} from 'shared/web';

import {captureBaseQueryError} from './captureBaseQueryError';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';
import {PROMPT_BRIDGE_SPENDING_LIMIT_ERROR_CODE} from './constants/PROMPT_BRIDGE_SPENDING_LIMIT_ERROR_CODE';
import {getPromptBridgeRequestHeaders} from './getPromptBridgeRequestHeaders';
import {refreshAccessToken} from './refreshAccessToken';
import {resolvePromptBridgeBaseUrl} from './resolvePromptBridgeBaseUrl';

/**
 * Resolved once at module load. The dev-only loopback override is read from `localStorage`, and
 * `resolvePromptBridgeBaseUrl` ignores it in production builds, so this can never redirect real
 * traffic. Changing the override therefore requires a page reload.
 */
const fetchBaseQueryInstance = fetchBaseQuery({
  baseUrl: resolvePromptBridgeBaseUrl(),
  credentials: 'include',
  prepareHeaders: (previousHeaders, api) => {
    const headers = getPromptBridgeRequestHeaders(previousHeaders, api.getState());
    const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export interface PromptBridgeCustomFetchArgs extends FetchArgs {
  queryArg?: Record<string, unknown> | void;
}

/** Only the spending-limit middleware answers with this body code; a bare 429 does not. */
const isSpendingLimitError = (error: FetchBaseQueryError) =>
  error.status === 429 &&
  isPlainObj(error.data) &&
  'code' in error.data &&
  error.data.code === PROMPT_BRIDGE_SPENDING_LIMIT_ERROR_CODE;

export const promptBridgeBaseQuery: BaseQueryFn<
  PromptBridgeCustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.queryArg,
      schema: extraOptions.requestSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'request',
      },
    });
  }

  let result = await fetchBaseQueryInstance(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshed = await refreshAccessToken(api.dispatch, api.getState);

    if (refreshed) {
      result = await fetchBaseQueryInstance(args, api, extraOptions);
    }
  }

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'response',
      },
    });
  }

  if (result.error) {
    if (isSpendingLimitError(result.error)) {
      showNotification.warning(i18next.t('entity.promptBridge.notifications.spendingLimitReached'));
    }
    if (typeof result.error.status === 'number' && result.error.status >= 500) {
      captureBaseQueryError(result.error, args, api);
    }
  }

  return result;
};
